import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, isAfter, isBefore, startOfDay, parseISO } from "date-fns";
import { db, auth } from "../firebase/config";
import { collection, getDocs, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  GraduationCap,
  BookOpen,
  DollarSign,
  Calendar,
  Bed,
  Eye,
  CalendarDays,
  MessageSquare,
  Plus,
  Send,
  AlertTriangle,
  Bell,
  Sun,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";


/* ────────── INTERFACES ────────── */
interface Affiliation {
  role: string;
  schoolID?: string;
}

/* ────────── DATA ────────── */

const todayStats = [
  { label: "Students Present", value: "1,156", total: "1,234", percentage: 94 },
  { label: "Teachers on Duty", value: "82", total: "87", percentage: 94 },
  { label: "Classes Today", value: "45", total: "48", percentage: 94 },
];

const financeData = [
  { month: "Jan", collected: 120000, expenses: 85000 },
  { month: "Feb", collected: 135000, expenses: 92000 },
  { month: "Mar", collected: 127450, expenses: 88000 },
  { month: "Apr", collected: 142000, expenses: 95000 },
  { month: "May", collected: 138000, expenses: 91000 },
  { month: "Jun", collected: 145000, expenses: 97000 },
];

const announcements = [
  {
    id: 1,
    title: "Parent-Teacher Conference Scheduled",
    content:
      "All parents are invited to attend the quarterly conference on March 15th.",
    time: "2 hours ago",
    urgent: false,
  },
  {
    id: 2,
    title: "School Closure Notice",
    content: "School will be closed on March 10th due to maintenance work.",
    time: "1 day ago",
    urgent: true,
  },
  {
    id: 3,
    title: "New Sports Equipment Arrived",
    content: "The new sports equipment for the gymnasium has been delivered.",
    time: "3 days ago",
    urgent: false,
  },
];



const chartConfig = {
  collected: { label: "Fees Collected", color: "#3B82F6" },
  expenses: { label: "Expenses", color: "#EF4444" },
};


/* ────────── COMPONENT ────────── */
const AdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // State management
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  
  // Dashboard data state
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [totalSubjects, setTotalSubjects] = useState(0);
  const [feesCollected, setFeesCollected] = useState(0);
  const [dormOccupancy, setDormOccupancy] = useState({ occupied: 0, total: 0 });
  const [cardDesign, setCardDesign] = useState(14);
  const [cardScale] = useState(80);
  const [, setEasterEggCount] = useState(0);
  const [showEasterEggToggle, setShowEasterEggToggle] = useState(false);
  const [enabledProviders, setEnabledProviders] = useState<string[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);

  // Fetch upcoming events
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const eventsRef = collection(db, `schools/${schoolID}/events`);
    const unsubscribe = onSnapshot(eventsRef, (snapshot) => {
      const today = startOfDay(new Date());
      const nextWeek = addDays(today, 7);
      
      const events = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as any))
        .filter(event => {
          if (!event.date) return false;
          // Handle both string dates (YYYY-MM-DD) and timestamps
          const eventDate = typeof event.date === 'string' ? parseISO(event.date) : event.date.toDate ? event.date.toDate() : new Date(event.date);
          // Check if valid date
          if (isNaN(eventDate.getTime())) return false;
          // Keep only future events within next 7 days (including today)
          return (isAfter(eventDate, today) || eventDate.getTime() === today.getTime()) && isBefore(eventDate, nextWeek);
        })
        .sort((a, b) => {
          const dateA = typeof a.date === 'string' ? parseISO(a.date) : a.date.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = typeof b.date === 'string' ? parseISO(b.date) : b.date.toDate ? b.date.toDate() : new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });

      setUpcomingEvents(events);
    }, (error) => {
      console.error("Error fetching events:", error);
    });

    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  // Authentication and claims verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user);
          const claims = idTokenResult.claims as { affiliations?: Affiliation[] };
          const affiliations = claims.affiliations || [];
          const adminAffiliation = affiliations.find((aff) => aff.role === "admin" || aff.role === "owner");
          
          if (adminAffiliation?.schoolID) {
            setSchoolID(adminAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            toast({
              title: "Access Denied",
              description: "You lack permissions to view this dashboard.",
              variant: "destructive",
              duration: 5000,
            });
            navigate("/login-page");
          }
        } catch (error) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Authentication Error",
            description: "Failed to verify permissions.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      } else {
        toast({
          title: "Authentication Required",
          description: "Please log in to continue.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  // Fetch total students
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const unsubscribe = onSnapshot(studentsRef, (snapshot) => {
      setTotalStudents(snapshot.size);
    }, (error) => {
      console.error("Error fetching students:", error);
    });

    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  // Fetch total teachers
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const teachersRef = collection(db, `schools/${schoolID}/teachers`);
    const unsubscribe = onSnapshot(teachersRef, (snapshot) => {
      setTotalTeachers(snapshot.size);
    }, (error) => {
      console.error("Error fetching teachers:", error);
    });

    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  // Fetch total subjects
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const subjectsRef = collection(db, `schools/${schoolID}/subjects`);
    const unsubscribe = onSnapshot(subjectsRef, (snapshot) => {
      setTotalSubjects(snapshot.size);
    }, (error) => {
      console.error("Error fetching subjects:", error);
    });

    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  // Load enabled providers list once we have schoolID
  useEffect(() => {
    const loadProviders = async () => {
      if (!schoolID) { setEnabledProviders([]); return; }
      try {
        const cfgSnap = await getDoc(doc(db, `schools/${schoolID}/paymentConfig/main`));
        const enabled: string[] = [];
        if (cfgSnap.exists()) {
          const data = cfgSnap.data() as any;
          const methods: any[] = data?.paymentMethods || [];
          for (const m of methods) {
            if (m?.enabled && m?.provider) enabled.push(String(m.provider));
          }
        }
        const fallback = ['mpesa','equity','kcb','cash','card'];
        const list = (enabled.length ? enabled : fallback).filter((p,i,a)=>a.indexOf(p)===i);
        setEnabledProviders(list);
      } catch (e:any) {
        setEnabledProviders(['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank']);
      }
    };
    loadProviders();
  }, [schoolID]);

  // Fetch fees collected
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    if (!enabledProviders.length) return;
    const fetchFeesCollected = async () => {
      try {
        const functions = getFunctions();
        let totalBalance = 0;
        for (const provider of enabledProviders) {
          try {
            let getBalance: any;
            switch(provider) {
              case 'mpesa':
                getBalance = httpsCallable(functions, 'getMpesaBalance');
                break;
              case 'equity':
                getBalance = httpsCallable(functions, 'getEquityBalance');
                break;
              case 'kcb':
                getBalance = httpsCallable(functions, 'getKcbBalance');
                break;
              case 'telkom':
                getBalance = httpsCallable(functions, 'getTelkomBalance');
                break;
              case 'airtel':
                getBalance = httpsCallable(functions, 'getAirtelBalance');
                break;
              case 'standardChartered':
                getBalance = httpsCallable(functions, 'getStandardCharteredBalance');
                break;
              case 'familyBank':
                getBalance = httpsCallable(functions, 'getFamilyBankBalance');
                break;
              default:
                continue;
            }
            const result = await getBalance({ schoolID });
            const data = result.data as { success: boolean; balance?: number };
            if (data.success && data.balance) {
              totalBalance += data.balance;
            }
          } catch (err) {
            // Skip providers that are not configured
          }
        }
        setFeesCollected(totalBalance);
      } catch (error) {
        setFeesCollected(0);
      }
    };
    fetchFeesCollected();
  }, [schoolID, hasValidClaims, enabledProviders]);

  // Fetch dorm occupancy
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchDormOccupancy = async () => {
      try {
        const dormsRef = collection(db, `schools/${schoolID}/dorms`);
        const dormsSnapshot = await getDocs(dormsRef);
        
        let totalCapacity = 0;
        let totalOccupied = 0;

        for (const dormDoc of dormsSnapshot.docs) {
          const dormData = dormDoc.data();
          const dormName = dormData.name;
          totalCapacity += dormData.capacity || 0;

          // Count students assigned to this dorm
          const studentsRef = collection(db, `schools/${schoolID}/students`);
          const q = query(studentsRef, where("dormitory", "==", dormName));
          const studentsSnapshot = await getDocs(q);
          totalOccupied += studentsSnapshot.size;
        }

        setDormOccupancy({ occupied: totalOccupied, total: totalCapacity });
      } catch (error) {
        console.error("Error fetching dorm occupancy:", error);
      }
    };

    fetchDormOccupancy();
  }, [schoolID, hasValidClaims]);

  const getIconColor = (index: number) => {
    const colors = [
      'text-blue-500',
      'text-green-500',
      'text-purple-500',
      'text-yellow-500',
      'text-red-500',
      'text-indigo-500',
      'text-pink-500',
      'text-cyan-500',
      'text-orange-500',
      'text-teal-500'
    ];
    return colors[index % colors.length];
  };

  const getBgColor = (index: number) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-cyan-500',
      'bg-orange-500',
      'bg-teal-500'
    ];
    return colors[index % colors.length];
  };

  const getGradientFrom = (index: number) => {
    const colors = [
      'from-blue-500',
      'from-green-500',
      'from-purple-500',
      'from-yellow-500',
      'from-red-500',
      'from-indigo-500',
      'from-pink-500',
      'from-cyan-500',
      'from-orange-500',
      'from-teal-500'
    ];
    return colors[index % colors.length];
  };

  const getGradientTo = (index: number) => {
    const colors = [
      'to-purple-600',
      'to-emerald-600',
      'to-pink-600',
      'to-orange-600',
      'to-rose-600',
      'to-violet-600',
      'to-fuchsia-600',
      'to-sky-600',
      'to-amber-600',
      'to-cyan-600'
    ];
    return colors[index % colors.length];
  };

  const renderCard = (stat: any, index: number) => {
    const Icon = stat.icon;
    const iconColor = getIconColor(index);
    const bgColor = getBgColor(index);
    const gradientFrom = getGradientFrom(index);
    const gradientTo = getGradientTo(index);
    let design = cardDesign;
    // Only allow easter egg designs if toggle is shown
    if (!showEasterEggToggle && [7,12,15,16].includes(cardDesign)) design = 8;
    switch (design) {
      case 1:
        // Modern Gradient with Glassmorphism
        return (
          <div key={index} className={`relative overflow-hidden bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl p-4 sm:p-6 shadow-2xl backdrop-blur-sm`}>
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -mr-12 sm:-mr-16 -mt-12 sm:-mt-16"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                {stat.trend && (
                  <span className="text-white/90 text-xs sm:text-sm font-semibold bg-white/20 px-2 sm:px-3 py-1 rounded-full">
                    {stat.trend}
                  </span>
                )}
              </div>
              <div className="text-2xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.value}</div>
              <h3 className="text-white/90 font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">{stat.title}</h3>
              <p className="text-white/70 text-xs sm:text-sm">{stat.description}</p>
            </div>
          </div>
        );

      case 2:
        // Neumorphism Design
        return (
          <div key={index} className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-3xl p-4 sm:p-6 shadow-[8px_8px_16px_#d1d1d1,-8px_-8px_16px_#ffffff] dark:shadow-[8px_8px_16px_#1a1a1a,-8px_-8px_16px_#2a2a2a]">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl flex items-center justify-center shadow-inner`}>
                <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-gray-800 dark:text-white mb-1 sm:mb-2">
              {stat.value}
            </div>
            <h3 className="text-gray-700 dark:text-gray-300 font-bold text-sm sm:text-base mb-0.5 sm:mb-1">
              {stat.title}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">{stat.description}</p>
            {stat.trend && (
              <div className={`mt-3 sm:mt-4 ${bgColor} text-white text-xs font-semibold px-2 sm:px-3 py-1 rounded-full inline-block`}>
                {stat.trend}
              </div>
            )}
          </div>
        );

      case 3:
        // Minimalist Border Accent
        return (
          <div key={index} className={`bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 border-l-4 ${bgColor.replace('bg-', 'border-')} shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 ${bgColor}/10 rounded-lg flex items-center justify-center`}>
                <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${iconColor}`} />
              </div>
              {stat.trend && <span className="text-green-600 text-xs font-bold">{stat.trend}</span>}
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-0.5 sm:mb-1">
              {stat.value}
            </div>
            <h3 className="text-gray-600 dark:text-gray-300 font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1">
              {stat.title}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-xs">{stat.description}</p>
          </div>
        );

      case 4:
        // Card with Top Colored Bar
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-blue-400 via-blue-500 to-purple-500"></div>
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${bgColor} rounded-full flex items-center justify-center`}>
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                {stat.trend && (
                  <div className="text-right">
                    <span className="text-green-600 font-bold text-xs sm:text-sm">{stat.trend}</span>
                  </div>
                )}
              </div>
              <div className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {stat.value}
              </div>
              <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-base mb-0.5">
                {stat.title}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
            </div>
          </div>
        );

      case 5:
        // Floating Icon Design (needs extra margin)
        return (
          <div key={index} className="relative bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-xl mt-8">
            <div className="absolute -top-4 sm:-top-6 left-4 sm:left-6">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl flex items-center justify-center shadow-lg transform rotate-6 hover:rotate-0 transition-transform`}>
                <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
            <div className="mt-6 sm:mt-8">
              {stat.trend && (
                <div className="flex justify-end mb-1 sm:mb-2">
                  <span className="text-green-600 font-bold text-xs sm:text-sm bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                    {stat.trend}
                  </span>
                </div>
              )}
              <div className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {stat.value}
              </div>
              <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-base mb-0.5">
                {stat.title}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
            </div>
          </div>
        );

      case 6:
        // Split Background Design
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            <div className="flex flex-col sm:flex-row">
              <div className={`w-full sm:w-1/3 bg-gradient-to-br ${gradientFrom} ${gradientTo} p-4 sm:p-6 flex items-center justify-center`}>
                <Icon className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
              </div>
              <div className="w-full sm:w-2/3 p-4 sm:p-6">
                {stat.trend && (
                  <div className="flex justify-end mb-1 sm:mb-2">
                    <span className="text-green-600 font-bold text-xs">{stat.trend}</span>
                  </div>
                )}
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                  {stat.value}
                </div>
                <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1">
                  {stat.title}
                </h3>
                <p className="text-gray-500 text-xs">{stat.description}</p>
              </div>
            </div>
          </div>
        );

      case 7:
        // Soft Shadow with Hover Effect (Easter Egg)
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-xl flex items-center justify-center shadow-md`}>
                <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              {stat.trend && (
                <div className="text-right">
                  <span className="text-green-600 font-extrabold text-base sm:text-lg">{stat.trend}</span>
                  <p className="text-gray-400 text-xs">growth</p>
                </div>
              )}
            </div>
            <div className="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2">
              {stat.value}
            </div>
            <h3 className="text-gray-700 dark:text-gray-300 font-bold text-xs sm:text-base mb-0.5">{stat.title}</h3>
            <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
          </div>
        );

      case 8:
        // Card with Progress Bar (Default)
        return (
          <div key={index} className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${bgColor} rounded-lg flex items-center justify-center`}>
                <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              {stat.trend && <span className="text-green-600 font-bold text-xs sm:text-sm">{stat.trend}</span>}
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              {stat.value}
            </div>
            <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-sm mb-2 sm:mb-3">
              {stat.title}
            </h3>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
              <div className={`${bgColor} h-1.5 sm:h-2 rounded-full`} style={{ width: "75%" }}></div>
            </div>
            <p className="text-gray-500 text-xs">{stat.description}</p>
          </div>
        );

      case 9:
        // Outlined Card with Dotted Border
        return (
          <div key={index} className={`bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border-2 border-dashed ${bgColor.replace('bg-', 'border-')}/30 hover:border-solid transition-all`}>
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className={`w-9 h-9 sm:w-11 sm:h-11 ${bgColor}/10 rounded-full flex items-center justify-center`}>
                <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${iconColor}`} />
              </div>
              {stat.trend && (
                <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold px-2 py-1 rounded-full">
                  {stat.trend}
                </div>
              )}
            </div>
            <div className={`text-2xl sm:text-4xl font-bold ${iconColor} mb-1 sm:mb-2`}>{stat.value}</div>
            <h3 className="text-gray-800 dark:text-gray-200 font-semibold text-xs sm:text-base mb-0.5 sm:mb-1">
              {stat.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">{stat.description}</p>
          </div>
        );

      case 10:
        // Card with Corner Accent
        return (
          <div key={index} className="relative bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg overflow-hidden">
            <div className={`absolute top-0 right-0 w-20 h-20 sm:w-24 sm:h-24 ${bgColor} rounded-bl-full opacity-10`}></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${bgColor} rounded-xl flex items-center justify-center shadow-md`}>
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                {stat.trend && (
                  <span className="text-green-600 font-bold text-xs sm:text-sm bg-green-50 dark:bg-green-900/20 px-2 sm:px-3 py-1 rounded-full">
                    {stat.trend}
                  </span>
                )}
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {stat.value}
              </div>
              <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-base mb-0.5">
                {stat.title}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
            </div>
          </div>
        );

      case 11:
        // Card with Diagonal Background
        return (
          <div key={index} className="relative bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 transform -skew-y-6"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl flex items-center justify-center shadow-lg`}>
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                {stat.trend && <span className="text-green-600 font-bold text-xs sm:text-sm">{stat.trend}</span>}
              </div>
              <div className="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2">
                {stat.value}
              </div>
              <h3 className="text-gray-700 dark:text-gray-300 font-bold text-xs sm:text-base mb-0.5">{stat.title}</h3>
              <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
            </div>
          </div>
        );

      case 12:
        // Material Design Elevated Card (Easter Egg)
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] transition-shadow duration-300">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${bgColor} rounded-lg flex items-center justify-center shadow-md`}>
                <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
            <div className="mb-1">
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {stat.title}
              </span>
            </div>
            <div className="flex items-baseline gap-2 mb-1 sm:mb-2">
              <span className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                {stat.value}
              </span>
              {stat.trend && <span className="text-green-600 font-semibold text-xs sm:text-sm">{stat.trend}</span>}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">{stat.description}</p>
          </div>
        );

      case 14:
        // Compact Horizontal Layout (Easter Egg)
        return (
          <div key={index} className={`bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-md border border-gray-200 dark:border-gray-700 hover:border-blue-500 transition-colors`}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Icon className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </span>
                  <span className="text-green-600 font-bold text-xs bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                    {stat.trend}
                  </span>
                </div>
                <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-sm mb-0.5">
                  {stat.title}
                </h3>
                <p className="text-gray-500 text-xs">{stat.description}</p>
              </div>
            </div>
          </div>
        );

      case 15:
        // Card with Bottom Stat Bar (Easter Egg)
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-md">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                {stat.trend && <span className="text-green-600 font-bold text-xs sm:text-sm">{stat.trend}</span>}
              </div>
              <div className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {stat.value}
              </div>
              <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-base mb-0.5">
                {stat.title}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">{stat.description}</p>
            </div>
            <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} h-1 sm:h-1.5`}></div>
          </div>
        );

      case 16:
        // Card with Circular Progress (Easter Egg)
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-sm mb-2">
                  {stat.title}
                </h3>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                  {stat.value}
                </div>
                <p className="text-gray-500 text-xs mb-2">{stat.description}</p>
                <span className="text-green-600 font-bold text-xs bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                  {stat.trend}
                </span>
              </div>
              <div className="relative w-16 h-16">
                <svg className="transform -rotate-90 w-16 h-16">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    className="text-gray-200 dark:text-gray-700"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - 0.75)}`}
                    className={iconColor}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon className={`h-6 w-6 text-blue-500`} />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        // Fallback to design 8
        const Icon8 = stat.icon;
        return (
          <div key={index} className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${bgColor} rounded-lg flex items-center justify-center`}>
                <Icon8 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              {stat.trend && <span className="text-green-600 font-bold text-xs sm:text-sm">{stat.trend}</span>}
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              {stat.value}
            </div>
            <h3 className="text-gray-700 dark:text-gray-300 font-semibold text-xs sm:text-sm mb-2 sm:mb-3">
              {stat.title}
            </h3>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
              <div className={`${bgColor} h-1.5 sm:h-2 rounded-full`} style={{ width: "75%" }}></div>
            </div>
            <p className="text-gray-500 text-xs">{stat.description}</p>
          </div>
        );
    }
  };

  const summaryStats = [
    {
      icon: Users,
      title: "Total Students",
      value: isLoading ? <Skeleton className="h-6 w-16" /> : totalStudents.toLocaleString(),
      description: "Active enrolled students",
      color: "bg-blue-500",
      trend: "",
    },
    {
      icon: GraduationCap,
      title: "Total Teachers",
      value: isLoading ? <Skeleton className="h-6 w-16" /> : totalTeachers.toLocaleString(),
      description: "Teaching staff members",
      color: "bg-green-500",
      trend: "",
    },
    {
      icon: BookOpen,
      title: "Total Subjects",
      value: isLoading ? <Skeleton className="h-6 w-16" /> : totalSubjects.toLocaleString(),
      description: "Active curriculum subjects",
      color: "bg-purple-500",
      trend: "",
    },
    {
      icon: DollarSign,
      title: "Fees Collected",
      value: isLoading ? <Skeleton className="h-6 w-20" /> : `KES ${feesCollected.toLocaleString()}`,
      description: "Total balance across providers",
      color: "bg-yellow-500",
      trend: "",
    },
    {
      icon: Calendar,
      title: "Upcoming Events",
      value: "7",
      description: "Next 30 days",
      color: "bg-red-500",
      trend: "3 urgent",
    },
    {
      icon: Bed,
      title: "Dorm Occupancy",
      value: isLoading ? <Skeleton className="h-6 w-16" /> : dormOccupancy.total > 0 ? `${Math.round((dormOccupancy.occupied / dormOccupancy.total) * 100)}%` : "N/A",
      description: isLoading ? "Loading..." : `${dormOccupancy.occupied}/${dormOccupancy.total} beds occupied`,
      color: "bg-indigo-500",
      trend: "",
    },
  ];

  return (
    <main className="flex-1 p-3 sm:p-6 overflow-y-auto overflow-x-hidden bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 cursor-pointer select-none"
              onClick={() => {
                setEasterEggCount((c) => {
                  const next = c + 1;
                  if (next >= 20) setShowEasterEggToggle(true);
                  return next;
                });
              }}
            >
              Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Overview of school operations and key metrics
            </p>
          </div>
          {showEasterEggToggle && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Easter Egg:</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                value={cardDesign}
                onChange={e => setCardDesign(Number(e.target.value))}
                aria-label="Select card design"
              >
                <option value={7}>Design 7</option>
                <option value={8}>Design 8 (Default)</option>
                <option value={12}>Design 12</option>
                <option value={14}>Design 14</option>
                <option value={15}>Design 15</option>
                <option value={16}>Design 16</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 -mb-24 sm:mb-1.5"
        style={{ transform: `scale(${cardScale / 100})`, transformOrigin: 'top left', width: `${10000 / cardScale}%` }}
      >
        {summaryStats.map((stat, index) => renderCard(stat, index))}
      </div>

      {/* Today's Overview & Finance Snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-6">
        {/* Today's Overview */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="flex items-center gap-2 dark:text-white text-base sm:text-lg">
              <CalendarDays className="h-5 w-5 text-blue-500" />
              Today's Overview
            </CardTitle>
            <CardDescription className="dark:text-gray-300 text-xs sm:text-sm">
              Current day statistics and weather
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-4">
              {todayStats.map((stat, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {stat.label}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {stat.value} of {stat.total}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-12 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${stat.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-blue-600">
                      {stat.percentage}%
                    </span>
                  </div>
                </div>
              ))}

              {/* Weather Widget */}
              <div className="flex items-center gap-3 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Sun className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                    Sunny, 24°C
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-300">
                    Perfect day for outdoor activities
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Finance Snapshot */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="flex items-center gap-2 dark:text-white text-base sm:text-lg">
              <DollarSign className="h-5 w-5 text-green-500" />
              Finance Snapshot
            </CardTitle>
            <CardDescription className="dark:text-gray-300 text-xs sm:text-sm">
              Financial overview and trends
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Fees Collected (This Term)
                </span>
                <span className="font-bold text-green-600">$127,450</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Outstanding Balances
                </span>
                <span className="font-bold text-red-600">$23,120</span>
              </div>
            </div>
            <ChartContainer config={chartConfig} className="h-[150px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={financeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={10} />
                  <YAxis fontSize={10} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="collected" fill="#3B82F6" />
                  <Bar dataKey="expenses" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Announcements & Upcoming Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-6">
        {/* Recent Announcements */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 dark:text-white text-base sm:text-lg">
                <MessageSquare className="h-5 w-5 text-purple-500" />
                Recent Announcements
              </CardTitle>
              <Button variant="outline" size="sm" className="text-xs">
                <Eye className="h-3 w-3 mr-1" />
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-3">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className={`p-3 rounded-lg border-l-4 ${
                    announcement.urgent
                      ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                      : "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm dark:text-white">
                        {announcement.title}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {announcement.content}
                      </p>
                    </div>
                    {announcement.urgent && (
                      <AlertTriangle className="h-4 w-4 text-red-500 ml-2" />
                    )}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {announcement.time}
                  </span>
                </div>
              ))}
            </div>
            <Button className="w-full mt-4" size="sm">
              <Send className="h-3 w-3 mr-2" />
              Send New Message
            </Button>
          </CardContent>
        </Card>

        {/* Upcoming Items & Alerts */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="flex items-center gap-2 dark:text-white text-base sm:text-lg">
              <Bell className="h-5 w-5 text-orange-500" />
              Upcoming Items & Alerts
            </CardTitle>
            <CardDescription className="dark:text-gray-300 text-xs sm:text-sm">
              Next 7 days overview
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-3">
              {upcomingEvents.length === 0 ? (
                 <p className="text-sm text-gray-500 text-center py-4">No upcoming events for the next 7 days.</p>
              ) : (
                upcomingEvents.map((item, index) => {
                  const itemDate = typeof item.date === 'string' ? parseISO(item.date) : item.date.toDate ? item.date.toDate() : new Date(item.date);
                  const isUrgent = item.type === 'exam' || isBefore(itemDate, addDays(new Date(), 2));

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg m-2"
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isUrgent ? "bg-red-500" : "bg-blue-500"
                        }`}
                      >
                        {item.type === "exam" ? (
                          <BookOpen className="h-4 w-4 text-white" />
                        ) : (
                          <Calendar className="h-4 w-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm dark:text-white">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {format(itemDate, 'MMMM d, yyyy')}
                        </p>
                      </div>
                      {isUrgent && (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="flex items-center gap-2 dark:text-white text-base sm:text-lg">
            <Plus className="h-5 w-5 text-green-500" />
            Quick Actions & Shortcuts
          </CardTitle>
          <CardDescription className="dark:text-gray-300 text-xs sm:text-sm">
            Frequently used actions
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto p-4 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => navigate("/students")}
            >
              <Users className="h-6 w-6 text-blue-500" />
              <span className="text-xs">Register Student</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto p-4 hover:bg-green-50 dark:hover:bg-green-900/20"
              onClick={() => navigate("/dashboard/Staff")}
            >
              <GraduationCap className="h-6 w-6 text-green-500" />
              <span className="text-xs">Add Teacher</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto p-4 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              onClick={() => navigate("/dashboard/academics")}
            >
              <Calendar className="h-6 w-6 text-purple-500" />
              <span className="text-xs">View Timetable</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto p-4 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              onClick={() => navigate("/communication")}
            >
              <MessageSquare className="h-6 w-6 text-orange-500" />
              <span className="text-xs">Send Message</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
};

export default AdminDashboard;
